import { useMemo } from 'react';
import {
  Query,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useToast } from '../../hooks/useToast';
import { usePageVisibility } from '../../hooks/usePageVisibility';
import {
  checkActiveSyncJobs,
  duplicateDefaultTemplates,
  createPartnerCopyOfDefaultAutomation,
  getAutomationTemplates,
  getAutomationTemplate,
  getDefaultAutomationMetadata,
  syncAutomationTemplates,
  updateAutomationTemplate,
} from './engage-automations-tab/template-api';
import type { UpdateAutomationTemplateParams } from './engage-automations-tab/templates.types';

export const templateKeys = {
  all: ['automationTemplates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
  defaultMetadata: () => [...templateKeys.all, 'defaultMetadata'] as const,
  syncStatus: () => [...templateKeys.all, 'syncStatus'] as const,
};

/**
 * Hook to fetch all automation templates for the authenticated partner
 */
export function useAutomationTemplates() {
  return useQuery({
    queryKey: templateKeys.lists(),
    queryFn: () => getAutomationTemplates(),
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });
}

/**
 * Hook to update an automation template
 */
export function useUpdateAutomationTemplate() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: string;
      params: UpdateAutomationTemplateParams;
    }) => updateAutomationTemplate(id, params),
    onSuccess: (_, variables) => {
      // Invalidate templates list
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });
      // Invalidate specific template
      queryClient.invalidateQueries({
        queryKey: templateKeys.detail(variables.id),
      });
      addToast({
        title: 'Saved',
        description: 'Template changes saved successfully.',
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to save changes.',
        variant: 'destructive',
      });
    },
  });
}

export function useDuplicateDefaultTemplates() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => duplicateDefaultTemplates(),
    onSuccess: result => {
      // Invalidate templates list to refetch
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });

      // Show success or info toast based on result
      if (result.createdCount > 0) {
        addToast({
          title: 'Success!',
          description: `Added ${result.createdCount} default template(s)`,
          variant: 'default',
        });
      } else {
        addToast({
          title: 'No templates added',
          description:
            'No default templates were found to add. Make sure the default-templates.ts file is populated.',
          variant: 'default',
        });
      }
    },
    onError: (error: Error) => {
      console.error('Error adding default templates:', error);
      addToast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to add default templates. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useSyncAutomationTemplates() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncAutomationTemplates(),
    onSuccess: () => {
      // Invalidate sync status to trigger immediate refetch and reset backoff
      queryClient.invalidateQueries({ queryKey: templateKeys.syncStatus() });
      addToast({
        title: 'Sync Started',
        description: 'Automation template sync job has been queued',
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      console.error('Error syncing automation templates:', error);
      addToast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to sync automation templates. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useDefaultAutomationMetadata() {
  return useQuery({
    queryKey: templateKeys.defaultMetadata(),
    queryFn: () => getDefaultAutomationMetadata(),
    staleTime: 300000, // Consider data fresh for 5 minutes (metadata doesn't change often)
    gcTime: 600000, // Keep in cache for 10 minutes
  });
}

export function useCreatePartnerCopyOfDefaultAutomation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateName: string) =>
      createPartnerCopyOfDefaultAutomation(templateName),
    onSuccess: () => {
      // Invalidate templates list to refetch
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists(),
      });
    },
  });
}

export function useAutomationTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(id || ''),
    queryFn: () => getAutomationTemplate(id!),
    enabled: !!id,
    staleTime: 30000,
    gcTime: 300000,
  });
}

export function useActiveSyncJobsStatus() {
  const isVisible = usePageVisibility();

  const getRefetchInterval = useMemo(() => {
    let attempts = 0;
    let previousJobWasActive: boolean | undefined;

    return (query: Query<{ hasActiveJobs: boolean }>) => {
      const hasActiveJobs = query.state.data?.hasActiveJobs;
      const activeSyncsHaveJustAppeared =
        hasActiveJobs && !previousJobWasActive;
      attempts = activeSyncsHaveJustAppeared ? 0 : attempts + 1;

      previousJobWasActive = hasActiveJobs;

      const baseDelay = 2000;
      const maxDelay = 60 * 1000;
      const exponentialBackoff = Math.min(
        baseDelay * Math.pow(1.5, attempts),
        maxDelay,
      );
      return !hasActiveJobs || !isVisible ? false : exponentialBackoff;
    };
  }, [isVisible]);

  return useQuery({
    queryKey: templateKeys.syncStatus(),
    queryFn: () => checkActiveSyncJobs(),
    staleTime: 0,
    gcTime: 300000,
    refetchOnWindowFocus: true,
    refetchInterval: getRefetchInterval,
  });
}
